import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../../utils/API";
import Swal from "sweetalert2";

function ClientQuotePage() {
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("service_id");
  const token = searchParams.get("token");

  const [serviceData, setServiceData] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  // Grouped: selectedParts[itemId] = { [groupIndex]: code }
  // Legacy:  selectedParts[itemId] = code
  const [selectedParts, setSelectedParts] = useState({});
  const [loading, setLoading] = useState(true);

  // ---- helpers ----
  const isGroupedParts = (parts) =>
    Array.isArray(parts) &&
    parts.length > 0 &&
    typeof parts[0] === "object" &&
    parts[0] !== null &&
    "options" in parts[0] &&
    Array.isArray(parts[0].options);

  const toNumberOrNull = (val) => {
    if (val === "" || val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const parsed = parseFloat(String(val).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatBRL = (n) =>
    (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  /**
   * Total corrente do que o cliente escolheu até agora.
   *
   * Recalculado a cada clique, e não lido de item.price, porque item.price é o
   * valor cheio gravado no orçamento original — ficaria defasado no instante em
   * que o cliente trocasse uma peça ou recusasse um serviço.
   *
   * Mão de obra = hht * valor_hht. Peças = opção escolhida × quantidade.
   */
  const buildSummary = () => {
    const empty = { labour: 0, parts: 0, total: 0, accepted: 0, declined: 0 };
    if (!serviceData) return empty;

    const rate = Number(serviceData.valor_hht) || 0;
    let labour = 0;
    let parts = 0;
    let accepted = 0;
    let declined = 0;

    serviceData.items.forEach((item) => {
      const itemId = item.service_item_id;
      if (!selectedItems.includes(itemId)) {
        declined += 1;
        return;
      }
      accepted += 1;
      labour += (Number(item.hht) || 0) * rate;

      const itemParts = Array.isArray(item.parts) ? item.parts : [];
      const priceOf = (opt) => {
        const unit = toNumberOrNull(opt.price);
        return unit === null ? 0 : unit * (Number(opt.quantity) || 1);
      };

      if (isGroupedParts(itemParts)) {
        const byGroup = selectedParts[itemId] || {};
        itemParts.forEach((group, gidx) => {
          const ref = byGroup[gidx] !== undefined ? byGroup[gidx] : byGroup[String(gidx)];
          if (ref === undefined) return;
          const opt = (group.options || []).find(
            (o) => (o.service_parts_id ?? o.code) === ref
          );
          if (opt) parts += priceOf(opt);
        });
      } else {
        const ref = selectedParts[itemId];
        const opt = itemParts.find((o) => (o.service_parts_id ?? o.code) === ref);
        if (opt) parts += priceOf(opt);
      }
    });

    return { labour, parts, total: labour + parts, accepted, declined };
  };

  // Check if the quote has already been processed (approved or rejected)
  const isProcessed = serviceData && (serviceData.service.status === 2 || serviceData.service.status === -2);
  const isApproved = serviceData && serviceData.service.status === 2;
  const isRejected = serviceData && serviceData.service.status === -2;

  useEffect(() => {
    if (serviceId && token) fetchServiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, token]);

  const fetchServiceDetails = () => {
    setLoading(true);
    API.getClientQuote(API.cancelToken(), serviceId, token)
      .then((res) => {
        setServiceData(res.data);

        // Default: select all items
        const initialSelectedItems = res.data.items.map((i) => i.service_item_id);
        setSelectedItems(initialSelectedItems);

        // Build selectedParts map (prefer stable service_parts_id; fall back to code)
        const initial = {};
        res.data.items.forEach((item) => {
          const parts = Array.isArray(item.parts) ? item.parts : [];
          if (isGroupedParts(parts)) {
            const byGroup = {};
            parts.forEach((group, gidx) => {
              const sel = (group.options || []).find((o) => o.selected);
              if (sel) {
                if (sel.service_parts_id != null) byGroup[gidx] = sel.service_parts_id;
                else if (sel.code) byGroup[gidx] = sel.code;
              }
            });
            initial[item.service_item_id] = byGroup;
          } else {
            // legacy flat
            const sel = parts.find((p) => p.selected);
            if (sel) {
              if (sel.service_parts_id != null) initial[item.service_item_id] = sel.service_parts_id;
              else if (sel.code) initial[item.service_item_id] = sel.code;
            }
          }
        });
        setSelectedParts(initial);

        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
        Swal.fire("Erro", "Erro ao carregar orçamento", "error");
      });
  };

  const handleItemToggle = (itemId) => {
    if (isProcessed) return;
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleGroupOption = (itemId, groupIndex, ref) => {
    if (isProcessed) return;
    setSelectedParts((prev) => ({
      ...prev,
      [itemId]: {
        ...(typeof prev[itemId] === "object" ? prev[itemId] : {}),
        [groupIndex]: ref,
      },
    }));
  };

  const handleFlatOption = (itemId, ref) => {
    if (isProcessed) return;
    setSelectedParts((prev) => ({ ...prev, [itemId]: ref }));
  };

  const validateBeforeSubmit = () => {
    if (!serviceData) return false;

    const missing = [];
    for (const item of serviceData.items) {
      const itemId = item.service_item_id;
      if (!selectedItems.includes(itemId)) continue;

      const parts = Array.isArray(item.parts) ? item.parts : [];
      if (isGroupedParts(parts)) {
        const byGroup = selectedParts[itemId] || {};
        parts.forEach((group, gidx) => {
          const hasOptions = (group.options || []).some((o) => {
            const hasDesc = (o.description || "").trim() !== "";
            const hasCode = (o.code || "").trim() !== "";
            const priceVal = parseFloat(o.price);
            const hasPrice = !isNaN(priceVal) && priceVal > 0;
            return hasDesc || hasCode || hasPrice;
          });
          const selected = byGroup[gidx] !== undefined || byGroup[String(gidx)] !== undefined;
          if (hasOptions && !selected) missing.push({ itemId, gidx });
        });
      } else {
        if (parts.length > 0 && !selectedParts[itemId]) missing.push({ itemId, gidx: 0 });
      }
    }

    if (missing.length > 0) {
      Swal.fire({
        title: "Selecione as peças",
        text: "Você precisa selecionar uma opção em cada grupo de peças para todos os serviços escolhidos.",
        icon: "warning",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (isProcessed) return;
    if (!validateBeforeSubmit()) return;

    Swal.fire({
      title: "Enviando suas escolhas...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    API.submitClientQuote(API.cancelToken(), serviceId, {
      token,
      selected_items: selectedItems,
      selected_parts: selectedParts,
    })
      .then(() => {
        Swal.fire("Sucesso", "Seu orçamento foi enviado!", "success");
        fetchServiceDetails(); // Reload to reflect new status
      })
      .catch((err) => {
        console.error(err);
        Swal.fire("Erro", "Erro ao enviar suas escolhas", "error");
      });
  };

  const handleRejectQuote = () => {
    if (isProcessed) return;

    Swal.fire({
      title: "Tem certeza?",
      text: "Ao rejeitar, este orçamento será marcado como recusado e você não poderá mais realizar alterações.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sim, rejeitar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Rejeitando orçamento...",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        API.rejectClientQuote(API.cancelToken(), serviceId, { token })
          .then(() => {
            Swal.fire("Rejeitado!", "O orçamento foi rejeitado com sucesso.", "success");
            fetchServiceDetails(); // Reload to reflect new status
          })
          .catch((err) => {
            console.error(err);
            Swal.fire("Erro", "Erro ao rejeitar orçamento", "error");
          });
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p>Carregando...</p>
      </div>
    );
  }

  if (!serviceData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p>Orçamento não encontrado ou token inválido.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-10 px-6 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">
        Revisão de Orçamento - OS #{serviceId}
      </h2>

      {/* Status banner when already processed */}
      {isApproved && (
        <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-300">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <p className="text-green-800 font-semibold text-lg">Orçamento Aprovado</p>
              <p className="text-green-700 text-sm">Este orçamento já foi aprovado. As opções selecionadas estão exibidas abaixo apenas para consulta.</p>
            </div>
          </div>
        </div>
      )}
      {isRejected && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-300">
          <div className="flex items-center gap-3">
            <span className="text-2xl">❌</span>
            <div>
              <p className="text-red-800 font-semibold text-lg">Orçamento Rejeitado</p>
              <p className="text-red-700 text-sm">Este orçamento foi rejeitado. Não é possível realizar alterações.</p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8 bg-white p-4 rounded shadow">
        <p className="text-gray-700 font-medium">Descrição geral:</p>
        <p className="text-gray-600">{serviceData.service.problem_description}</p>
      </div>

      {serviceData.items.map((item) => {
        const parts = Array.isArray(item.parts) ? item.parts : [];
        const grouped = isGroupedParts(parts);
        const itemNotSelected = !selectedItems.includes(item.service_item_id);

        return (
          <div
            key={item.service_item_id}
            className={`border border-gray-200 p-6 rounded-lg shadow-sm mb-6 bg-white ${isProcessed ? "opacity-80" : ""}`}
          >
            <div className="flex items-start justify-between mb-3 gap-3">
              <h3 className={`text-lg font-semibold ${itemNotSelected ? "text-gray-400 line-through" : "text-gray-800"}`}>
                {item.service_item_title}
              </h3>
              <span className="text-sm text-gray-500 whitespace-nowrap">{item.hht}h</span>
            </div>

            {/* Escolha explícita por serviço.
                Antes existia só uma caixa de seleção, que o cliente lia como
                "marcado por padrão" e não como uma decisão a tomar. Duas opções
                lado a lado deixam claro que recusar o serviço é uma alternativa
                legítima. */}
            {!isProcessed && (
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => { if (itemNotSelected) handleItemToggle(item.service_item_id); }}
                  className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition ${
                    !itemNotSelected
                      ? "border-green-600 bg-green-50 text-green-800"
                      : "border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  Realizar este serviço
                </button>
                <button
                  type="button"
                  onClick={() => { if (!itemNotSelected) handleItemToggle(item.service_item_id); }}
                  className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition ${
                    itemNotSelected
                      ? "border-red-500 bg-red-50 text-red-700"
                      : "border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  Não realizar
                </button>
              </div>
            )}

            {isProcessed && (
              <p className={`text-sm mb-3 font-medium ${itemNotSelected ? "text-red-600" : "text-green-700"}`}>
                {itemNotSelected ? "Serviço recusado" : "Serviço aprovado"}
              </p>
            )}

            {item.service_item_annotation && (
              <p className="text-sm text-gray-600 mb-3">
                {item.service_item_annotation}
              </p>
            )}

            {parts.length > 0 && (
              <div className="ml-1 space-y-5">
                <p className="text-sm font-medium text-gray-700">
                  {isProcessed
                    ? "Opção(ões) selecionada(s):"
                    : "Escolha a(s) opção(ões) desejada(s) — uma por grupo:"}
                </p>

                {grouped ? (
                  // GROUPED RENDER
                  parts.map((group, gidx) => {
                    const options = group.options || [];
                    const groupName = (group.name || `Peça ${gidx + 1}`).trim();
                    const nameAttr = `item_${item.service_item_id}_group_${gidx}`;

                    return (
                      <div key={gidx} className="border border-gray-100 rounded p-3">
                        <p className="text-sm text-gray-800 mb-2 font-semibold">
                          {groupName}
                        </p>

                        {options.length === 0 && (
                          <p className="text-sm text-gray-500">Sem opções.</p>
                        )}

                        {options.map((opt, idx) => {
                          const priceNum = toNumberOrNull(opt.price);
                          const optRef = opt.service_parts_id ?? opt.code;
                          const isChecked =
                            (selectedParts[item.service_item_id] || {})[gidx] === optRef;
                          return (
                            <label
                              key={idx}
                              className={`flex items-center mb-2 p-2 rounded border-2 transition ${
                                isChecked
                                  ? "border-green-600 bg-green-50 shadow-sm"
                                  : "border-transparent"
                              } ${isProcessed ? "cursor-default" : "cursor-pointer hover:bg-gray-50"}`}
                            >
                              <input
                                type="radio"
                                name={nameAttr}
                                value={optRef}
                                checked={isChecked}
                                onChange={() =>
                                  handleGroupOption(item.service_item_id, gidx, optRef)
                                }
                                disabled={itemNotSelected || isProcessed}
                                className={`mr-3 text-green-600 focus:ring-green-500 ${isProcessed ? "cursor-not-allowed" : ""}`}
                              />
                              <div className={`text-sm ${isChecked ? "text-green-900" : "text-gray-800"}`}>
                                <div className={`font-medium ${isChecked ? "font-semibold" : ""} flex flex-wrap items-center gap-2`}>
                                  <span>{opt.description || "(sem descrição)"}</span>
                                  {opt.selected && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                      ★ Recomendada pela oficina
                                    </span>
                                  )}
                                  {isChecked && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-600 text-white">
                                      Selecionada
                                    </span>
                                  )}
                                </div>
                                <div className={isChecked ? "text-green-800" : "text-gray-600"}>
                                  Código: {opt.code || "-"} &nbsp;•&nbsp; Qtd:{" "}
                                  {opt.quantity ?? 1}
                                  {priceNum !== null && (
                                    <>
                                      {" "}
                                      • {(opt.quantity ?? 1)} × R$ {priceNum.toFixed(2)} ={" "}
                                      <span className="text-green-700 font-medium">
                                        R$ {(priceNum * (Number(opt.quantity) || 1)).toFixed(2)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })
                ) : (
                  // LEGACY FLAT RENDER
                  parts.map((opt, idx) => {
                    const nameAttr = `item_${item.service_item_id}_flat`;
                    const priceNum = toNumberOrNull(opt.price);
                    const optRef = opt.service_parts_id ?? opt.code;
                    const isChecked = selectedParts[item.service_item_id] === optRef;
                    return (
                      <label
                        key={idx}
                        className={`flex items-center mb-2 p-2 rounded border-2 transition ${
                          isChecked
                            ? "border-green-600 bg-green-50 shadow-sm"
                            : "border-transparent"
                        } ${isProcessed ? "cursor-default" : "cursor-pointer hover:bg-gray-50"}`}
                      >
                        <input
                          type="radio"
                          name={nameAttr}
                          value={optRef}
                          checked={isChecked}
                          onChange={() => handleFlatOption(item.service_item_id, optRef)}
                          disabled={itemNotSelected || isProcessed}
                          className={`mr-3 text-green-600 focus:ring-green-500 ${isProcessed ? "cursor-not-allowed" : ""}`}
                        />
                        <div className={`text-sm ${isChecked ? "text-green-900" : "text-gray-800"}`}>
                          <div className={`font-medium ${isChecked ? "font-semibold" : ""} flex flex-wrap items-center gap-2`}>
                            <span>{opt.description || "(sem descrição)"}</span>
                            {opt.selected && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                ★ Recomendada pela oficina
                              </span>
                            )}
                            {isChecked && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-600 text-white">
                                Selecionada
                              </span>
                            )}
                          </div>
                          <div className={isChecked ? "text-green-800" : "text-gray-600"}>
                            Código: {opt.code || "-"} &nbsp;•&nbsp; Qtd: {opt.quantity ?? 1}
                            {priceNum !== null && (
                              <>
                                {" "}
                                • {(opt.quantity ?? 1)} × R$ {priceNum.toFixed(2)} ={" "}
                                <span className="text-green-700 font-medium">
                                  R$ {(priceNum * (Number(opt.quantity) || 1)).toFixed(2)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Prévia dinâmica dos valores + ações.
          Fica fixo no rodapé para o cliente enxergar o total enquanto escolhe,
          em vez de descobrir o valor só depois de aprovar. */}
      {!isProcessed && (() => {
        const s = buildSummary();
        return (
          <div className="sticky bottom-0 -mx-4 mt-8 border-t-2 border-gray-200 bg-white px-4 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
            <p className="text-sm font-semibold text-gray-800 mb-2">
              Resumo da sua escolha
            </p>

            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Mão de obra</span>
                <span>{formatBRL(s.labour)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Peças</span>
                <span>{formatBRL(s.parts)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2 mt-2 text-base font-bold text-gray-900">
                <span>Total</span>
                <span className="text-green-700">{formatBRL(s.total)}</span>
              </div>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              {s.accepted} serviço(s) aprovado(s)
              {s.declined > 0 && `, ${s.declined} recusado(s)`}. Valor sujeito a
              confirmação; os termos do contrato serão enviados após a aprovação.
            </p>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleSubmit}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded font-medium"
              >
                Aprovar {formatBRL(s.total)}
              </button>
              <button
                onClick={handleRejectQuote}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded font-medium"
              >
                Rejeitar
              </button>
            </div>
          </div>
        );
      })()}

      {/* Já processado: mostra o total aprovado, sem ações */}
      {isProcessed && isApproved && (
        <div className="mt-8 border-t-2 border-gray-200 pt-4">
          <div className="flex justify-between text-base font-bold text-gray-900">
            <span>Total aprovado</span>
            <span className="text-green-700">{formatBRL(buildSummary().total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientQuotePage;
